import { useEffect } from 'react'
import { Outlet, NavLink, useLocation } from 'react-router-dom'
import { Sidebar } from './Sidebar'
import { Topbar } from './Topbar'
import { FloatingAgent } from '@/components/FloatingAgent'
import { useUIStore } from '@/stores/uiStore'
import { useSidebarStore } from '@/stores/sidebarStore'
import { useAuth, useLogout } from '@/api/hooks/useAuth'
import { useSystemSettings } from '@/api/hooks/useSystemSettings'
import { useLanguage } from '@/i18n/LanguageContext'
import { cn } from '@/lib/utils'
import { isStagingMode, isReviewMode, isForexReview } from '@/lib/staging'
import { BarChart3, Shield, Send, LogOut, Code } from 'lucide-react'

export function AppLayout() {
  const sidebarOpen = useUIStore((s) => s.sidebarOpen)
  const setSidebarOpen = useUIStore((s) => s.setSidebarOpen)
  const pinned = useSidebarStore((s) => s.pinned)
  const { data: user } = useAuth()
  const { data: settings } = useSystemSettings()
  const { setLanguage } = useLanguage()
  const location = useLocation()
  const logout = useLogout()

  useEffect(() => {
    if (user?.username === 'vini' || isStagingMode() || isForexReview()) setLanguage('en')
  }, [user?.username])

  // Prevent browser from opening dropped files in new tabs (global)
  useEffect(() => {
    const prevent = (e: DragEvent) => e.preventDefault()
    document.addEventListener('dragover', prevent)
    document.addEventListener('drop', prevent)
    return () => {
      document.removeEventListener('dragover', prevent)
      document.removeEventListener('drop', prevent)
    }
  }, [])

  useEffect(() => {
    document.body.style.pointerEvents = ''
    document.body.style.removeProperty('pointer-events')
    if (document.body.style.overflow === 'hidden') {
      document.body.style.removeProperty('overflow')
    }
    document.documentElement.removeAttribute('data-scroll-locked')
  }, [location.pathname])

  const reviewMode = isReviewMode()
  const forex = isForexReview()

  if (reviewMode) {
    const initial = (user?.name || 'U').charAt(0).toUpperCase()
    const reviewNav = [
      { to: '/gerenciador', label: forex ? 'Page Manager' : 'Gerenciador', icon: Shield },
      { to: '/broadcast', label: 'Broadcast', icon: Send },
    ]
    return (
      <div className="flex h-screen">
        {/* Sidebar */}
        <aside className="hidden md:flex w-[220px] flex-col bg-sidebar border-r border-sidebar-border shrink-0">
          {/* Brand */}
          <div className="flex items-center gap-3 px-4 py-4">
            {forex ? (
              <div className="flex h-9 w-9 items-center justify-center rounded-[10px]" style={{ background: 'linear-gradient(135deg, #1877F2, #1565C0)' }}>
                <BarChart3 className="h-5 w-5 text-white" />
              </div>
            ) : (
              <img src="/images/logo.png" alt="Blissf" className="h-9 w-9 shrink-0 rounded-[10px] object-cover" />
            )}
            <span className="text-[15px] font-bold tracking-tight text-foreground truncate">{forex ? 'ForexBrokerReview' : 'Blissf'}</span>
          </div>

          {/* Nav */}
          <nav className="flex-1 px-2 py-2 space-y-1">
            <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
              {forex ? 'Navigation' : 'Menu'}
            </p>
            {reviewNav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => cn(
                  'flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  isActive ? 'bg-sidebar-accent text-primary' : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                )}
              >
                <item.icon className="h-4 w-4" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          {/* API Status */}
          <div className="px-3 py-2">
            <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 px-3 py-2">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-[11px] font-medium text-emerald-400">API Connected</span>
              </div>
              <p className="text-[10px] text-emerald-400/60 mt-0.5">Graph API v22.0</p>
            </div>
          </div>

          {/* Debug mode indicator */}
          <div className="px-3 py-2">
            <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 px-3 py-2">
              <div className="flex items-center gap-2">
                <Code className="h-3 w-3 text-amber-400" />
                <span className="text-[11px] font-medium text-amber-400">Debug Mode ON</span>
              </div>
              <p className="text-[10px] text-amber-400/60 mt-0.5">Raw API responses visible</p>
            </div>
          </div>

          {/* User */}
          <div className="shrink-0 border-t border-sidebar-border p-3">
            <div className="flex items-center gap-3 rounded-[10px] bg-secondary px-3 py-2.5">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-sidebar-accent text-sm font-bold text-primary">
                {initial}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[11px] font-semibold text-foreground">{user?.name}</div>
                <div className="text-[10px] text-muted-foreground">{forex ? 'Administrator' : 'Admin'}</div>
              </div>
              <button onClick={() => logout.mutate()} className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive shrink-0" title="Sign out">
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </div>
        </aside>

        {/* Main */}
        <div className="flex flex-1 flex-col min-w-0">
          {/* Mobile header */}
          <div className="md:hidden flex items-center gap-3 px-4 py-3 border-b border-border">
            {forex ? (
              <div className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: 'linear-gradient(135deg, #1877F2, #1565C0)' }}>
                <BarChart3 className="h-4 w-4 text-white" />
              </div>
            ) : (
              <img src="/images/logo.png" alt="Blissf" className="h-8 w-8 rounded-lg" />
            )}
            <span className="text-sm font-bold text-foreground">{forex ? 'ForexBrokerReview' : 'Blissf'}</span>
            <div className="flex-1" />
            {reviewNav.map((item) => (
              <NavLink key={item.to} to={item.to} className={({ isActive }) => cn('px-3 py-1.5 rounded-md text-xs font-medium', isActive ? 'bg-sidebar-accent text-primary' : 'text-muted-foreground')}>
                {item.label}
              </NavLink>
            ))}
          </div>

          <main className="flex-1 overflow-y-auto p-6 md:p-8">
            <div className="mx-auto w-full max-w-[1100px]">
              <Outlet key={location.pathname} />
            </div>
          </main>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-screen">
      {/* Mobile overlay with backdrop-blur */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Desktop sidebar — fixed, expands on hover via Sidebar component */}
      <div className="fixed inset-y-0 left-0 z-50 hidden md:block border-r border-sidebar-border">
        <Sidebar />
      </div>

      {/* Mobile sidebar — slides from left, always expanded */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50 border-r border-sidebar-border transition-transform duration-200 md:hidden',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <Sidebar mobile />
      </div>

      {/* Main content — offset by the collapsed/pinned sidebar width */}
      <div
        className={cn(
          'flex flex-1 flex-col min-w-0 transition-[margin] duration-200',
          pinned ? 'md:ml-[220px]' : 'md:ml-16'
        )}
      >
        <Topbar />

        {/* Staging banner */}
        {isStagingMode() && (
          <div className="bg-blue-600 text-white">
            <div className="mx-auto px-4 py-2.5 text-center">
              <p className="text-sm font-semibold tracking-wide">Meta App Review — Staging Environment</p>
            </div>
          </div>
        )}

        {/* Maintenance banner */}
        {settings?.maintenance_mode && !isStagingMode() && (
          <div className="bg-amber-500 text-black">
            <div className="mx-auto px-4 py-4 text-center">
              <p className="text-lg font-bold tracking-wide uppercase">Sistema em manuten&#231;&#227;o</p>
              <p className="text-sm font-medium opacity-80">Evite criar postagens ou fazer altera&#231;&#245;es. Voltamos em breve!</p>
            </div>
          </div>
        )}

        {/* Meta API down banner (só admin) */}
        {settings?.meta_api_down && user?.isAdmin && !isStagingMode() && (
          <div className="bg-red-600 text-white">
            <div className="mx-auto px-4 py-3 text-center">
              <p className="text-sm font-bold tracking-wide uppercase">
                &#9888;&#65039; API do Meta fora do ar
                {settings.meta_api_down_posting && settings.meta_api_down_broadcast
                  ? ' (postagem + broadcast)'
                  : settings.meta_api_down_posting
                    ? ' (postagem)'
                    : settings.meta_api_down_broadcast
                      ? ' (broadcast)'
                      : ''}
              </p>
              <p className="text-xs font-medium opacity-90">O token de conex&#227;o caiu ou expirou. Atualize em Admin &#8594; Apps do Meta.</p>
            </div>
          </div>
        )}

        {/* Trial banner */}
        {!isStagingMode() && user?.trialEndsAt && (() => {
          const diff = Math.ceil((new Date(user.trialEndsAt!).getTime() - Date.now()) / 86400000)
          const expired = diff <= 0
          return (
            <div className={cn('text-white text-center py-2.5 px-4', expired ? 'bg-red-600' : 'bg-emerald-600')}>
              {expired ? (
                <p className="text-sm font-semibold">Seu periodo de teste expirou. Entre em contato para continuar usando.</p>
              ) : (
                <p className="text-sm font-semibold">Periodo de teste gratuito — {diff === 1 ? 'ultimo dia!' : `${diff} dias restantes`}</p>
              )}
            </div>
          )
        })()}

        <main className="flex-1 overflow-y-auto p-6 md:p-8">
          <div className="mx-auto w-full max-w-[1100px]">
            <Outlet key={location.pathname} />
          </div>
        </main>
      </div>

      {!isStagingMode() && <FloatingAgent />}
    </div>
  )
}
